import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Camera, MagnifyingGlass, ArrowLeft, FilePdf, Sparkle, PaperPlaneTilt, X, CheckCircle, FileImage, UploadSimple, Trash } from "@phosphor-icons/react";
import DeleteWorkflowButton from "@/components/DeleteWorkflowButton";
import McKinseyButton from "@/components/McKinseyButton";

const LS_KEY_SR = "span_sighting_draft_v1";

const REC = [
  { v: "further_investigation", label: "Further investigation" },
  { v: "raid_action", label: "Raid action" },
  { v: "civil_action", label: "Civil action" },
  { v: "cd_letter", label: "C&D letter" },
  { v: "intel_only", label: "Intel only" },
];
const STATUS_COLORS = {
  submitted:"bg-[var(--surface-3)] text-[var(--text-primary)]",
  under_review:"bg-amber-50 text-amber-700",
  cover_note_generated:"bg-blue-50 text-blue-700",
  sent_to_client:"bg-teal-50 text-teal-700",
  approved:"bg-emerald-50 text-emerald-700",
  conditional:"bg-orange-50 text-orange-700",
  declined:"bg-red-50 text-red-700",
  intel_filed:"bg-[var(--surface-3)] text-[var(--text-primary)]",
};

export function SightingReports() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ status:"", brand:"" });
  const load = () => {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.brand) q.set("brand", filter.brand);
    api.get(`/sighting-reports?${q}`).then(r => setItems(r.data || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  return (
    <div className="p-6 space-y-4" data-testid="sighting-list-page">
      <div className="flex justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Camera size={12}/> Sighting reports</div>
          <h1 className="font-display font-bold text-3xl mt-1">Investigator → Client enforcement requests</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">Outbound: SPAN reports findings to the client and seeks authorization.</div>
        </div>
        <div className="flex gap-2">
          <select data-testid="sr-filter-status" value={filter.status} onChange={e=>setFilter({...filter, status:e.target.value})} className="text-[12px] hairline px-2 py-1">
            <option value="">All</option>
            <option value="submitted">Submitted</option>
            <option value="cover_note_generated">Cover note ready</option>
            <option value="sent_to_client">Sent</option>
            <option value="approved">Approved</option>
            <option value="conditional">Conditional</option>
            <option value="declined">Declined</option>
            <option value="intel_filed">Intel only</option>
          </select>
          <input data-testid="sr-filter-brand" placeholder="brand" value={filter.brand} onChange={e=>setFilter({...filter, brand:e.target.value})} className="text-[12px] hairline px-2 py-1 w-32"/>
          <Link to="/reports/sighting/new" className="span-btn text-[11px]">+ New Sighting</Link>
        </div>
      </div>
      <div className="card-flat p-0 overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-[var(--span-surface-3)] text-[9px] uppercase">
            <tr><th className="text-left px-2 py-2">SR ID</th><th className="text-left px-2 py-2">Brand</th>
              <th className="text-left px-2 py-2">Target</th><th className="text-left px-2 py-2">Rec</th>
              <th className="text-left px-2 py-2">Submitted by</th><th className="text-left px-2 py-2">Date</th>
              <th className="text-left px-2 py-2">Status</th><th className="text-left px-2 py-2">Case</th><th className="text-left px-2 py-2"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="px-3 py-6 text-center text-[var(--span-text-2)]">No reports yet. Click &quot;+ New Sighting&quot;.</td></tr>}
            {items.map(r => (
              <tr key={r.sighting_id} className={`border-t hairline hover:bg-[var(--span-surface-3)] ${r.deleted_at ? "opacity-60" : ""}`} data-testid={`sr-row-${r.sighting_id}`}>
                <td className="px-2 py-1.5"><Link to={`/reports/sighting/${r.sighting_id}`} className="font-mono text-[10px] text-[var(--span-accent)] hover:underline">{r.sighting_id}</Link></td>
                <td className="px-2 py-1.5 font-bold">{r.brand}</td>
                <td className="px-2 py-1.5 truncate max-w-[180px]" title={r.target?.name}>{r.target?.name}</td>
                <td className="px-2 py-1.5 text-[10px]">{r.recommendation?.replace(/_/g, " ")}</td>
                <td className="px-2 py-1.5 text-[var(--span-text-2)] truncate max-w-[120px]">{r.submitted_by_name || "—"}</td>
                <td className="px-2 py-1.5 text-[var(--span-text-2)]">{fmtDate(r.submitted_at)}</td>
                <td className="px-2 py-1.5"><span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 ${STATUS_COLORS[r.status] || "bg-[var(--surface-3)]"}`}>{r.status}</span></td>
                <td className="px-2 py-1.5 font-mono text-[10px]">{r.linked_case_code || <span className="text-amber-700 bg-amber-50 px-1.5 py-0.5">unlinked</span>}</td>
                <td className="px-2 py-1.5 text-right">
                  {!r.deleted_at ? (
                    <DeleteWorkflowButton entityType="sighting" entityId={r.sighting_id} entityLabel={r.sighting_id} onRequested={load} />
                  ) : <span className="badge badge-danger text-[8px]">deleted</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SightingReportNew() {
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [me, setMe] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const saveTimer = useRef(null);
  const [form, setForm] = useState({
    linked_case_id:"", brand:"", information_source:"Investigator",
    target: { type:"Retailer", supply_channel:"Physical market", name:"", address:"", maps_link:"", phone:"", mobile:"", email:"", website:"", online_presence:"", infringers:[{name:"",position:""}], connected_target:false },
    products: [{ description:"", quantity:"" }], total_quantity:"",
    annual_volume_estimate:"", sample_purchase:"NO",
    estimated_seizure_value:0, currency:"INR",
    intelligence_summary:"INTELLIGENCE SUMMARY — [TARGET NAME]\nOperation type: \nYears in operation: \nSupply chain position: \nKnown suppliers: \nCustomer base: \nOperation scale: \nConnected targets: \nConsumer safety risk: \nPrevious enforcement history: \nInvestigator observations: ",
    recommendation:"raid_action", planned_raid_date:"",
    images: [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/cases").then(r => setCases((r.data?.items || r.data || []).slice(0, 200)));
    api.get("/auth/me").then(r => setMe(r.data)).catch(()=>{});
    // Restore server draft first, then localStorage fallback
    api.get("/sighting-drafts").then(r => {
      if (r.data?.payload) { setForm(prev => ({ ...prev, ...r.data.payload })); setDraftRestored(true); return; }
      try {
        const ls = JSON.parse(localStorage.getItem(LS_KEY_SR) || "null");
        if (ls) { setForm(prev => ({ ...prev, ...ls })); setDraftRestored(true); }
      } catch {}
    });
  }, []);

  // Auto-save (debounced ~1.5s)
  useEffect(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try { localStorage.setItem(LS_KEY_SR, JSON.stringify(form)); } catch {}
      api.put("/sighting-drafts", { payload: form }).catch(() => {});
    }, 1500);
    return () => clearTimeout(saveTimer.current);
  }, [form]);

  const addImage = () => setForm({...form, images: [...form.images, { section:"exterior", filename:"", sha256:"", external_url:"" }]});
  const setImage = (i, patch) => {
    const out = [...form.images]; out[i] = {...out[i], ...patch}; setForm({...form, images: out});
  };
  const removeImage = (i) => setForm({...form, images: form.images.filter((_,idx)=>idx!==i)});

  const uploadFile = async (i, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert("Image must be ≤ 10 MB"); return; }
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/sighting-reports/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImage(i, {
        external_url: r.data.url,
        sha256: r.data.sha256,
        filename: r.data.filename,
      });
    } catch (e) {
      alert("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally { setUploadingIdx(null); }
  };

  const submit = async () => {
    if (form.intelligence_summary.length < 150) { setErr("Intelligence summary must be ≥150 chars"); return; }
    if (!form.target.maps_link.startsWith("https://")) { setErr("Maps link must start with https://"); return; }
    setBusy(true); setErr(null);
    try {
      const t = { ...form.target };
      if (!t.email) delete t.email;
      if (!t.website) delete t.website;
      if (!t.phone) delete t.phone;
      if (!t.mobile) delete t.mobile;
      if (!t.online_presence) delete t.online_presence;
      t.infringers = (t.infringers || []).filter(i => i.name || i.position);
      const payload = {
        ...form, target: t,
        products: form.products.filter(p => p.description),
        images: form.images.filter(img => img.external_url && img.sha256),
        reporter_name: me?.name,
      };
      if (me?.email) payload.reporter_email = me.email;
      const r = await api.post("/sighting-reports", payload);
      try { localStorage.removeItem(LS_KEY_SR); } catch {}
      nav(`/reports/sighting/${r.data.sighting_id}`);
    } catch (ex) {
      const d = ex.response?.data?.detail;
      const msg = Array.isArray(d)
        ? d.map(x => `${(x.loc || []).slice(-1)[0]}: ${x.msg}`).join("; ")
        : (typeof d === "string" ? d : "Submission failed");
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4" data-testid="sighting-new-page">
      <div className="flex items-center gap-2">
        <Link to="/reports/sighting" className="text-[12px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]"><ArrowLeft size={11} className="inline"/> Back</Link>
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Camera size={12}/> New sighting report</div>
          <h1 className="font-display font-bold text-2xl mt-1">File a sighting</h1>
          {draftRestored && <span className="badge badge-info text-[10px] mt-1" data-testid="sr-draft-restored">draft restored</span>}
        </div>
      </div>

      <div className="card-flat p-5 space-y-3 text-[12px]">
        <div className="font-display font-bold">Section A · Reporter &amp; Linkage</div>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="label-eyebrow block mb-0.5">Brand *</label>
            <input data-testid="sr-brand" required value={form.brand} onChange={e=>setForm({...form, brand:e.target.value})} className="w-full hairline px-2 py-1.5"/>
          </div>
          <div><label className="label-eyebrow block mb-0.5">Information source *</label>
            <select data-testid="sr-source" value={form.information_source} onChange={e=>setForm({...form, information_source:e.target.value})} className="w-full hairline px-2 py-1.5">
              {["Investigator","Informant","Law Enforcement","Public"].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><label className="label-eyebrow block mb-0.5">Link to case (optional)</label>
            <select data-testid="sr-case" value={form.linked_case_id} onChange={e=>setForm({...form, linked_case_id:e.target.value})} className="w-full hairline px-2 py-1.5">
              <option value="">— Unlinked (Case Mgr will link) —</option>
              {cases.map(c=><option key={c.case_id} value={c.case_id}>{c.case_code} · {c.title?.slice(0,50)}</option>)}
            </select>
          </div>
        </div>

        <div className="font-display font-bold pt-2">Section B · Target</div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label-eyebrow block mb-0.5">Target / company name *</label>
            <input data-testid="sr-target-name" required value={form.target.name} onChange={e=>setForm({...form, target:{...form.target, name:e.target.value}})} className="w-full hairline px-2 py-1.5"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="sr-target-type" value={form.target.type} onChange={e=>setForm({...form, target:{...form.target, type:e.target.value}})} className="hairline px-2 py-1.5">
              {["Manufacturer","Distributor","Wholesaler","Retailer","Online Seller","Dark Web","Storage","Other"].map(t=><option key={t}>{t}</option>)}
            </select>
            <select data-testid="sr-supply" value={form.target.supply_channel} onChange={e=>setForm({...form, target:{...form.target, supply_channel:e.target.value}})} className="hairline px-2 py-1.5">
              {["Physical market","Online marketplace","Social media","Direct supply","Import","Other"].map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div><label className="label-eyebrow block mb-0.5">Address *</label>
          <textarea data-testid="sr-target-address" required rows={2} value={form.target.address} onChange={e=>setForm({...form, target:{...form.target, address:e.target.value}})} className="w-full hairline px-2 py-1.5"/>
        </div>
        <div><label className="label-eyebrow block mb-0.5">Google Maps link * (https://)</label>
          <input data-testid="sr-maps" required value={form.target.maps_link} onChange={e=>setForm({...form, target:{...form.target, maps_link:e.target.value}})} placeholder="https://maps.google.com/?q=..." className="w-full hairline px-2 py-1.5"/>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input placeholder="Phone" value={form.target.phone} onChange={e=>setForm({...form, target:{...form.target, phone:e.target.value}})} className="hairline px-2 py-1.5"/>
          <input placeholder="Mobile" value={form.target.mobile} onChange={e=>setForm({...form, target:{...form.target, mobile:e.target.value}})} className="hairline px-2 py-1.5"/>
          <input placeholder="Email" value={form.target.email} onChange={e=>setForm({...form, target:{...form.target, email:e.target.value}})} className="hairline px-2 py-1.5"/>
        </div>

        <div className="font-display font-bold pt-2">Section C · Products</div>
        {form.products.map((p, i) => (
          <div key={i} className="grid grid-cols-[2fr_1fr_auto] gap-2">
            <input data-testid={`sr-product-${i}`} placeholder="Product description" value={p.description} onChange={e=>{const out=[...form.products]; out[i]={...out[i], description:e.target.value}; setForm({...form, products: out});}} className="hairline px-2 py-1.5"/>
            <input placeholder="Qty (e.g. 250 bottles)" value={p.quantity} onChange={e=>{const out=[...form.products]; out[i]={...out[i], quantity:e.target.value}; setForm({...form, products: out});}} className="hairline px-2 py-1.5"/>
            <button type="button" onClick={()=>setForm({...form, products: form.products.filter((_,x)=>x!==i)})} className="text-[10px] hairline px-2"><X size={10}/></button>
          </div>
        ))}
        <button type="button" data-testid="sr-add-product" onClick={()=>setForm({...form, products:[...form.products, {description:"",quantity:""}]})} className="text-[10px] hairline px-2 py-1">+ Add product</button>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="label-eyebrow block mb-0.5">Total quantity *</label>
            <input data-testid="sr-total-qty" required value={form.total_quantity} onChange={e=>setForm({...form, total_quantity:e.target.value})} className="w-full hairline px-2 py-1.5"/>
          </div>
          <div><label className="label-eyebrow block mb-0.5">Annual volume estimate</label>
            <input value={form.annual_volume_estimate} onChange={e=>setForm({...form, annual_volume_estimate:e.target.value})} className="w-full hairline px-2 py-1.5"/>
          </div>
          <div><label className="label-eyebrow block mb-0.5">Estimated seizure value</label>
            <div className="flex gap-1">
              <input type="number" value={form.estimated_seizure_value} onChange={e=>setForm({...form, estimated_seizure_value:parseFloat(e.target.value)||0})} className="hairline px-2 py-1.5 flex-1"/>
              <select value={form.currency} onChange={e=>setForm({...form, currency:e.target.value})} className="hairline px-1 py-1.5 w-16 text-[10px]">{["INR","USD"].map(c=><option key={c}>{c}</option>)}</select>
            </div>
          </div>
        </div>
        <div><label className="label-eyebrow block mb-0.5">Sample purchase *</label>
          <select data-testid="sr-sample" value={form.sample_purchase} onChange={e=>setForm({...form, sample_purchase:e.target.value})} className="w-full hairline px-2 py-1.5">
            {["YES","NO","NOT_POSSIBLE","BULK_ONLY"].map(v=><option key={v}>{v}</option>)}
          </select>
        </div>

        <div className="font-display font-bold pt-2">Section D · Intelligence summary *</div>
        <textarea data-testid="sr-intel" rows={9} value={form.intelligence_summary} onChange={e=>setForm({...form, intelligence_summary:e.target.value})} className="w-full hairline px-2 py-1.5 font-mono text-[11px]"/>
        <div className="text-[10px] text-[var(--span-text-2)]">{form.intelligence_summary.length} / 150 minimum</div>

        <div className="font-display font-bold pt-2">Section E · Evidence images</div>
        <div className="text-[10px] text-[var(--span-text-2)]">Upload up to 5 photos (≤10 MB each · PNG/JPEG/WEBP/HEIC). SHA-256 is computed server-side for chain of custody.</div>
        {form.images.map((img, i) => (
          <div key={i} className="grid grid-cols-[100px_1fr_2fr_1fr_auto] gap-2 items-center" data-testid={`sr-image-row-${i}`}>
            <select value={img.section} onChange={e=>setImage(i, { section: e.target.value })} className="hairline px-2 py-1.5 text-[10px]">{["exterior","interior","front","back","side","other"].map(s=><option key={s}>{s}</option>)}</select>
            {img.external_url ? (
              <div className="flex items-center gap-1">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${img.external_url}`} alt="thumb" className="h-10 w-10 object-cover hairline"/>
                <div className="text-[10px] truncate">{img.filename}</div>
              </div>
            ) : (
              <label className="hairline px-2 py-1.5 text-[10px] cursor-pointer hover:bg-[var(--span-surface-3)] text-center" data-testid={`sr-upload-label-${i}`}>
                <UploadSimple size={11} className="inline mr-1"/>{uploadingIdx === i ? "Uploading…" : "Choose file"}
                <input type="file" accept="image/*" className="hidden" data-testid={`sr-upload-${i}`}
                       onChange={e => uploadFile(i, e.target.files?.[0])} disabled={uploadingIdx===i}/>
              </label>
            )}
            <div className="text-[10px] text-[var(--span-text-2)] truncate" title={img.external_url}>{img.external_url || "Not uploaded"}</div>
            <div className="font-mono text-[9px] text-[var(--span-text-2)]" title={img.sha256}>{img.sha256 ? `${img.sha256.slice(0,12)}…` : "(awaiting)"}</div>
            <button type="button" onClick={()=>removeImage(i)} className="text-[10px] hairline px-2"><X size={10}/></button>
          </div>
        ))}
        <button type="button" data-testid="sr-add-image" onClick={addImage} disabled={form.images.length >= 5} className="text-[10px] hairline px-2 py-1 disabled:opacity-50">+ Add image ({form.images.length}/5)</button>

        <div className="font-display font-bold pt-2">Section F · Recommendation</div>
        <div className="grid grid-cols-5 gap-2">
          {REC.map(r => (
            <label key={r.v} className={`hairline px-3 py-2 text-[11px] text-center cursor-pointer ${form.recommendation===r.v ? "bg-emerald-50 border-emerald-400 font-bold" : ""}`}>
              <input type="radio" name="rec" value={r.v} checked={form.recommendation===r.v} onChange={e=>setForm({...form, recommendation:e.target.value})} data-testid={`sr-rec-${r.v}`} className="sr-only"/>
              {r.label}
            </label>
          ))}
        </div>
        {["raid_action","civil_action","cd_letter"].includes(form.recommendation) && (
          <div><label className="label-eyebrow block mb-0.5">Planned action date</label>
            <input type="date" value={form.planned_raid_date} onChange={e=>setForm({...form, planned_raid_date:e.target.value})} data-testid="sr-planned-date" className="hairline px-2 py-1.5"/>
          </div>
        )}

        {err && <div className="text-red-600 text-[12px]" data-testid="sr-err">{err}</div>}
        <div className="flex justify-end pt-3 border-t">
          <button data-testid="sr-submit" disabled={busy} onClick={submit} className="span-btn">{busy ? "Filing…" : "Submit sighting"}</button>
        </div>
      </div>
    </div>
  );
}

export function SightingReportDetail() {
  const { id } = useParams();
  const [sr, setSr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cnEdit, setCnEdit] = useState(null);
  const [lightbox, setLightbox] = useState(null);  // {index, items}

  const load = () => api.get(`/sighting-reports/${id}`).then(r => setSr(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const genCN = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/sighting-reports/${id}/generate-cover-note`);
      setCnEdit(r.data); await load();
    } catch (e) { alert(e.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };
  const approveCN = async () => {
    await api.post(`/cover-notes/${cnEdit.cover_note_id}/approve`);
    alert("Cover note approved. Now click Send.");
    await load();
    const fresh = await api.get(`/sighting-reports/${id}`);
    setCnEdit(fresh.data.cover_note);
  };
  const sendCN = async () => {
    try {
      const r = await api.post(`/cover-notes/${cnEdit.cover_note_id}/send`);
      alert(`Sent to ${r.data.sent_to}`);
      setCnEdit(null); await load();
    } catch (e) { alert(e.response?.data?.detail || "Send failed"); }
  };

  if (!sr) return <div className="p-8 text-[12px]">Loading…</div>;
  const eligible = !["further_investigation","intel_only"].includes(sr.recommendation);

  return (
    <div className="p-6 space-y-4" data-testid="sighting-detail-page">
      <div className="flex items-center gap-2">
        <Link to="/reports/sighting" className="text-[12px] hairline px-2 py-1"><ArrowLeft size={11} className="inline"/></Link>
        <div>
          <div className="label-eyebrow font-mono">{sr.sighting_id}</div>
          <h1 className="font-display font-bold text-2xl mt-0.5">{sr.target?.name}</h1>
          <div className="text-[11px] text-[var(--span-text-2)]">
            <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 ${STATUS_COLORS[sr.status]}`}>{sr.status}</span>
            {" · "}{sr.brand}{" · "}{fmtDate(sr.submitted_at)}
            {sr.linked_case_code && <>{" · "}<Link to={`/cases/${sr.linked_case_id}`} className="text-[var(--span-accent)] hover:underline">{sr.linked_case_code}</Link></>}
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <a data-testid="sr-pdf" href={`${process.env.REACT_APP_BACKEND_URL}/api/sighting-reports/${id}/pdf`} target="_blank" rel="noopener" className="text-[11px] hairline px-2 py-1 hover:bg-[var(--span-surface-3)]"><FilePdf size={11} className="inline"/> PDF</a>
          <McKinseyButton sourceType="sighting" sourceId={id}
            path={`/sightings/${id}/mckinsey/narrative`} label="McKinsey" />
          {eligible && !sr.cover_note_id && (
            <button data-testid="sr-gen-cn" disabled={busy} onClick={genCN} className="span-btn"><Sparkle size={11}/> Generate cover note</button>
          )}
          {sr.cover_note_id && !cnEdit && (
            <button data-testid="sr-open-cn" onClick={() => setCnEdit(sr.cover_note)} className="span-btn">Open cover note</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="card-flat p-4 text-[12px] space-y-2">
          <div className="font-display font-bold">Target</div>
          <div className="text-[var(--span-text-2)]">{sr.target?.address}</div>
          <div>Type: <b>{sr.target?.type}</b> · Channel: {sr.target?.supply_channel}</div>
          <a href={sr.target?.maps_link} target="_blank" rel="noopener" className="text-[11px] text-[var(--span-accent)] hover:underline">↗ Open in maps</a>
          <div className="pt-2 border-t mt-2">
            <div className="font-display font-bold">Products</div>
            {sr.products?.map((p,i)=><div key={i} className="text-[11px]">• {p.description} — {p.quantity}</div>)}
            <div className="text-[11px] mt-1"><b>Total:</b> {sr.total_quantity} · <b>Annual:</b> {sr.annual_volume_estimate}</div>
            <div className="text-[11px]"><b>Seizure value:</b> {sr.estimated_seizure_value?.toLocaleString()} {sr.currency}</div>
          </div>
        </div>
        <div className="card-flat p-4 text-[12px]">
          <div className="font-display font-bold mb-1">Intelligence summary</div>
          <div className="whitespace-pre-wrap text-[11px]">{sr.intelligence_summary}</div>
          <div className="mt-3 text-[11px]"><b>Recommendation:</b> {sr.recommendation?.replace(/_/g," ")}</div>
        </div>
      </div>

      {sr.evidence?.length > 0 && (
        <div className="card-flat p-4">
          <div className="font-display font-bold text-[13px] mb-2">Evidence · {sr.evidence.length} images</div>
          <div className="grid grid-cols-4 gap-3">
            {sr.evidence.map((e, idx) => {
              const items = sr.evidence.filter(ev => ev.external_url);
              const realIdx = items.findIndex(ev => ev.evidence_id === e.evidence_id);
              return (
              <div key={e.evidence_id} className="hairline p-2 text-[10px]" data-testid={`sr-evidence-${e.evidence_id}`}>
                {e.external_url ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${e.external_url.startsWith('http') ? '' : ''}${e.external_url}`}
                    alt={e.description}
                    onClick={() => setLightbox({ index: realIdx, items })}
                    className="w-full h-32 object-cover mb-1 cursor-zoom-in"
                    data-testid={`sr-evidence-thumb-${e.evidence_id}`}/>
                ) : <div className="w-full h-32 bg-[var(--span-surface-3)] flex items-center justify-center"><FileImage size={20}/></div>}
                <div className="font-bold">{e.section}</div>
                <div className="text-[var(--span-text-2)] truncate">{e.description}</div>
                <div className="font-mono text-[9px] text-[var(--span-text-2)]" title={e.sha256}>sha: {e.sha256?.slice(0,12)}…</div>
              </div>
            );})}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && lightbox.items.length > 0 && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center" data-testid="sr-lightbox"
             onClick={() => setLightbox(null)}
             onKeyDown={e => {
               if (e.key === "ArrowLeft") setLightbox(prev => ({...prev, index: Math.max(0, prev.index-1)}));
               if (e.key === "ArrowRight") setLightbox(prev => ({...prev, index: Math.min(prev.items.length-1, prev.index+1)}));
               if (e.key === "Escape") setLightbox(null);
             }} tabIndex={0}>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }} data-testid="lightbox-close"
                  className="absolute top-4 right-4 text-white hover:text-red-300"><X size={24}/></button>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(p => ({...p, index: Math.max(0, p.index-1)})); }}
                  disabled={lightbox.index === 0} data-testid="lightbox-prev"
                  className="absolute left-4 text-white text-3xl disabled:opacity-30">‹</button>
          <img src={`${process.env.REACT_APP_BACKEND_URL}${lightbox.items[lightbox.index].external_url}`}
               alt="" className="max-h-[85vh] max-w-[85vw] object-contain" onClick={e=>e.stopPropagation()}/>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(p => ({...p, index: Math.min(p.items.length-1, p.index+1)})); }}
                  disabled={lightbox.index === lightbox.items.length-1} data-testid="lightbox-next"
                  className="absolute right-4 text-white text-3xl disabled:opacity-30">›</button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-[12px] bg-black/50 px-3 py-1">
            {lightbox.index + 1} / {lightbox.items.length} · {lightbox.items[lightbox.index].section} · sha: {lightbox.items[lightbox.index].sha256?.slice(0,16)}…
          </div>
        </div>
      )}

      {/* Cover note slide-over */}
      {cnEdit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setCnEdit(null)}>
          <div onClick={e=>e.stopPropagation()} className="bg-white w-full max-w-3xl h-full overflow-auto p-5 space-y-3" data-testid="cover-note-panel">
            <div className="flex items-start justify-between">
              <div>
                <div className="label-eyebrow">Cover note v{cnEdit.version} · {cnEdit.status}</div>
                <h2 className="font-display font-bold text-lg mt-1">{cnEdit.subject_line}</h2>
                <div className="text-[10px] text-[var(--span-text-2)]">Engine: {cnEdit.engine || "claude-sonnet-4-5"}</div>
              </div>
              <button onClick={() => setCnEdit(null)}><X size={18}/></button>
            </div>
            <div className="hairline p-4 bg-[var(--span-surface-2)] text-[12px]" dangerouslySetInnerHTML={{__html: cnEdit.body_html}}/>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <button data-testid="cn-regenerate" onClick={genCN} disabled={busy} className="text-[11px] hairline px-3 py-1.5">Regenerate</button>
              {cnEdit.status === "draft" && (
                <button data-testid="cn-approve" onClick={approveCN} className="span-btn"><CheckCircle size={11}/> Approve</button>
              )}
              {cnEdit.status === "approved" && (
                <button data-testid="cn-send" onClick={sendCN} className="span-btn"><PaperPlaneTilt size={11}/> Send to client</button>
              )}
              {cnEdit.status === "sent" && (
                <div className="text-[11px] text-emerald-700 px-3 py-1.5"><CheckCircle size={11} className="inline mr-1"/> Sent to {cnEdit.sent_to}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Client authorization buttons (visible only if cover note sent) */}
      {sr.status === "sent_to_client" && sr.linked_case_id && (
        <div className="card-flat p-4" data-testid="sr-authorize-card">
          <div className="font-display font-bold mb-2">Capture client authorization</div>
          {["approved","conditional","declined"].map(d => (
            <button key={d} data-testid={`sr-auth-${d}`} onClick={async () => {
              const notes = window.prompt(`Notes for ${d} (optional):`) || "";
              await api.post(`/cases/${sr.linked_case_id}/authorize`, { sighting_report_id: sr.sighting_id, decision: d, decision_notes: notes });
              alert(`Marked ${d}`); load();
            }} className="span-btn span-btn-ghost mr-2">
              {d.charAt(0).toUpperCase() + d.slice(1)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
